import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TextInputComponent } from '../form-inputs/text-input/text-input.component';
import { TextAreaComponent } from '../form-inputs/text-area/text-area.component';
import { PasswordInputComponent } from '../form-inputs/password-input/password-input.component';
import { ToggleComponent } from '../form-inputs/toggle/toggle.component';
import { CheckboxComponent } from '../form-inputs/checkbox/checkbox.component';
import { SingleSelectComponent } from '../form-inputs/single-select/single-select.component';
import { DropDownComponent } from '../form-inputs/drop-down/drop-down.component';
import { MultiSelectComponent } from '../form-inputs/multi-select/multi-select.component';
import { AutoCompleteComponent } from '../form-inputs/auto-complete/auto-complete.component';
import { NumberInputComponent } from '../form-inputs/number-input/number-input.component';
import { ColorPickerInputComponent } from '../form-inputs/color-picker-input/color-picker-input.component';
import { IconPickerComponent } from '../form-inputs/icon-picker/icon-picker.component';
import { QuillViewComponent } from 'ngx-quill';
import { TextEditorComponent } from '../form-inputs/text-editor/text-editor.component';
import { ImageSelectComponent } from '../form-inputs/image-select/image-select.component';
import { PaymentSelectComponent } from '../form-inputs/payment-select/payment-select.component';
import { AccessibilityTextInputComponent } from '../form-inputs/accessibility-text-input/accessibility-text-input.component';
import { ButtonGroupComponent } from '../form-inputs/button-group/button-group.component';
import {
  BLACK,
  BY_PRIMARY_COLOR,
  CUSTOM,
  WHITE,
} from '../../../entities/coporate-design-entity';
import { Subscription } from 'rxjs';
import { DropZoneComponent } from '../form-inputs/drop-zone/drop-zone.component';
import { TranslationTextInputComponent } from '../form-inputs/translation-text-input/translation-text-input.component';
import { TranslationAccessibilityTextInputComponent } from '../form-inputs/translation-accessibility-text-input/translation-accessibility-text-input.component';
import { TranslationTextAreaComponent } from '../form-inputs/translation-text-area/translation-text-area.component';
import { LanguageSwitcherComponent } from '../form-inputs/language-switcher/language-switcher.component';
import { DateInputComponent } from '../form-inputs/date-input/date-input/date-input.component';
import { EnumToButtonOptionArrayPipe } from '../../../pipes/enum-to-button-option-array/enum-to-button-option-array.pipe';
import { EEntityType, ERelationType } from '@web/dtos';
import { TranslationTextEditorComponent } from '../form-inputs/translation-text-editor/translation-text-editor.component';
import { RelationDateInputComponent } from '../form-inputs/date-input/relation-date-input/relation-date-input.component';
import { ToFormControlPipe } from '../../../pipes/to-form-control/to-form-control.pipe';
import { MultiSelectDropDownComponent } from '../form-inputs/multi-select-drop-down/multi-select-drop-down.component';
import { ListInputComponent } from '../form-inputs/list-input/list-input.component';
import { SiteContextService } from '../../../services/site-context/site-context.service';
import { ThemeComponent } from '../../../theming';

@Component({
  selector: 'aw-form-inputs-display',
  imports: [
    CommonModule,
    TextInputComponent,
    ReactiveFormsModule,
    TextAreaComponent,
    PasswordInputComponent,
    ToggleComponent,
    CheckboxComponent,
    SingleSelectComponent,
    DropDownComponent,
    MultiSelectComponent,
    AutoCompleteComponent,
    NumberInputComponent,
    DropZoneComponent,
    ColorPickerInputComponent,
    IconPickerComponent,
    FormsModule,
    QuillViewComponent,
    TextEditorComponent,
    ButtonGroupComponent,
    ImageSelectComponent,
    PaymentSelectComponent,
    AccessibilityTextInputComponent,
    EnumToButtonOptionArrayPipe,
    TranslationTextInputComponent,
    TranslationAccessibilityTextInputComponent,
    TranslationTextAreaComponent,
    LanguageSwitcherComponent,
    DateInputComponent,
    TranslationTextEditorComponent,
    RelationDateInputComponent,
    ToFormControlPipe,
    MultiSelectDropDownComponent,
    ListInputComponent,
    ThemeComponent
  ],
  templateUrl: './form-inputs-display.component.html',
})
export class FormInputsDisplayComponent implements OnDestroy {
  protected readonly siteContextService: SiteContextService =
    inject(SiteContextService);
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  public readonly showCorporateDesign: WritableSignal<boolean> = signal(true);

  public text: FormControl<string | null> = new FormControl<string>('!!!!', [
    Validators.required,
  ]);
  public form: FormGroup = new FormGroup({
    iconPicker: new FormControl(undefined, [Validators.required]),
    text: new FormControl<string>('', [Validators.required]),
    accessibilityText: new FormControl<string>('', [Validators.required]),
    password: new FormControl<string>('', [Validators.required]),
    textArea: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(10),
    ]),
    number: new FormControl<number>(3, [
      Validators.required,
      Validators.min(1),
      Validators.max(10),
    ]),
    toggle: new FormControl<boolean>(false, [Validators.requiredTrue]),
    toggleDisabled: new FormControl<boolean>({value: true, disabled: true}),
    checkBox: new FormControl<boolean>(false, [Validators.requiredTrue]),
    date: new FormControl(undefined, [Validators.required]),
    dateTime: new FormControl(undefined, [Validators.required]),
    arrival: new FormControl(undefined, [
      Validators.required,
    ]) as unknown as FormControl<Date | undefined>,
    departure: new FormControl(undefined, [Validators.required]),
    dropdown: new FormControl(undefined, [
      Validators.required,
      Validators.min(5),
    ]),
    imageSelect: new FormControl(undefined, [Validators.required]),
    paymentSelect: new FormControl(undefined, [Validators.required]),
    dropdownName: new FormControl(undefined, [Validators.required]),
    dropdownNameAndValue: new FormControl(undefined, [
      Validators.required,
      Validators.min(5),
    ]),
    dropdownSearchable: new FormControl(undefined, [Validators.required]),
    singleSelect: new FormControl(undefined, [
      Validators.required,
      Validators.min(5),
    ]),
    singleSelect2: new FormControl(1, [
      Validators.required,
      Validators.min(5),
    ]),
    singleSelect3: new FormControl({value: 2, disabled: true}, [
      Validators.required,
      Validators.min(5),
    ]),
    singleSelect4: new FormControl({value: 3, disabled: true}, [
      Validators.required,
      Validators.min(5),
    ]),
    multiSelect: new FormControl({value: [1, 2], disabled: true}, [
      Validators.required,
      Validators.minLength(2),
    ]),
    multiSelectObject: new FormControl(undefined, [
      Validators.required,
      Validators.minLength(2),
    ]),
    multiSelectDropDown: new FormControl<string[]>([]),
    searchableMultiSelectDropDown: new FormControl<string[]>([]),
    textList: new FormControl<string[]>([
      'Eintrag 1',
      'Eintrag 2',
      'Eintrag 3',
    ]),
    searchableTextList: new FormControl<string[]>([
      'Eintrag 1',
      'Eintrag 2',
      'Eintrag 3',
      'Eintrag 4',
      'Eintrag 5',
      'Eintrag 6',
      'Eintrag 7',
      'Eintrag 8',
    ]),
    autoComplete: new FormControl(undefined, [
      Validators.required,
      Validators.min(5),
    ]),
    autoComplete2: new FormControl(undefined, [
      Validators.required,
      Validators.min(5),
    ]),
    autoComplete3: new FormControl(undefined, [Validators.required]),
    primaryFontColor: new FormControl(WHITE, [Validators.required]),
    color: new FormControl(undefined, [Validators.required]),
    fileUpload: new FormControl(undefined, [Validators.required]),
    quill: new FormControl<string>('', [Validators.required]),
    buttonGroup: new FormControl(undefined, [Validators.required]),
    translationText: new FormControl([], [Validators.required]),
    translationArea: new FormControl([], [Validators.required]),
    translationAccessible: new FormControl([], [Validators.required]),
    translationEditorAccessible: new FormControl([], [Validators.required]),
    language: new FormControl(undefined, [Validators.required]),
  });

  protected readonly custom: typeof CUSTOM = CUSTOM;
  protected readonly white: string = WHITE;
  protected readonly black: string = BLACK;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly EntityType: typeof EEntityType = EEntityType;

  public buttonState: string = 'custom';
  public subscription: Subscription = new Subscription();

  constructor() {
    this.subscription.add(
      this.form.controls['primaryFontColor'].valueChanges.subscribe(value =>
        this.detectCustomOrPresetColor(value)
      )
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  detectCustomOrPresetColor(value: string): void {
    const colorMappings: { [key: string]: string } = {
      Weiß: '#FFFFFF',
      Schwarz: '#000000',
    };

    if (value === CUSTOM) {
      this.form.get('color')?.enable();
      this.buttonState = 'custom';
    } else if (colorMappings[value]) {
      this.form
        .get('color')
        ?.setValue(colorMappings[value], { emitEvent: true });
      this.form.get('color')?.disable();
      this.buttonState = colorMappings[value];
    } else if (value === BY_PRIMARY_COLOR) {
      this.form
        .get('color')
        ?.setValue(colorMappings[value], { emitEvent: true });
      this.form.get('color')?.disable();
      this.buttonState = colorMappings[value];
    }
  }
  toggle(): void {
    if (this.form.disabled) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.form.updateValueAndValidity();
  }

  toggleContext(): void {
    this.siteContextService.toggleContext();
    this.cdRef.detectChanges();
  }
}
